import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import reviewsData from "../data/reviews.json";
import RevCard from "./cards/RevCard";
import SEO from "./common/SEO";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";

const Reviews = () => {
  const { i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");

  const isEnglish = (i18n.language || "it").startsWith("en");
  const title = isEnglish ? "Press Reviews" : "Recensioni della Critica";
  const desc = isEnglish
    ? "Press reviews and critical acclaim for the concerts and recordings of the Nuova Orchestra da Camera Ferruccio Busoni."
    : "Rassegna stampa e recensioni della critica per i concerti e le incisioni della Nuova Orchestra da Camera Ferruccio Busoni.";

  const filteredReviews = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return reviewsData;

    return reviewsData.filter((r) => {
      const matchTitle = r.title && r.title.toLowerCase().includes(query);
      const matchContent = r.content && r.content.toLowerCase().includes(query);
      const matchAuthor = r.author && r.author.toLowerCase().includes(query);
      const matchSource = r.source && r.source.toLowerCase().includes(query);
      return matchTitle || matchContent || matchAuthor || matchSource;
    });
  }, [searchQuery]);

  return (
    <>
      <SEO title={title} description={desc} />
      <section className="section subpage-section">
        <div className="container subpage-container">
          {/* Header intro */}
          <div className="text-center mb-4">
            <p className="text-muted max-w-700 mx-auto mb-0">
              {isEnglish
                ? "Reviews and accolades from leading international music publications: Amadeus, Musica, Brilliant Classics, Klassik.com, Crescendo."
                : "Recensioni e giudizi dalle più autorevoli riviste e testate musicali: Amadeus, Rivista Musica, Brilliant Classics, Klassik.com, Crescendo."}
            </p>
          </div>

          {/* Search bar */}
          <div className="interactive-filter-card p-4 rounded-4 shadow-sm mb-5 bg-white border">
            <div className="row g-3 align-items-center">
              <div className="col-md-8">
                <div className="modern-search-wrapper">
                  <FontAwesomeIcon icon={faSearch} className="modern-search-icon" />
                  <input
                    type="text"
                    className="form-control modern-search-input"
                    placeholder={
                      isEnglish
                        ? "Search by critic, magazine, or album (e.g. Ralph Moore, Bottesini, Mozart)..."
                        : "Cerca per critico, rivista o opera (es. Ralph Moore, Bottesini, Mozart)..."
                    }
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Cerca recensione"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="modern-search-clear"
                      onClick={() => setSearchQuery("")}
                      aria-label="Cancella ricerca"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-4 text-md-end text-muted small">
                {isEnglish
                  ? `Showing ${filteredReviews.length} reviews`
                  : `Visualizzazione di ${filteredReviews.length} recensioni`}
              </div>
            </div>
          </div>

          {/* Reviews list */}
          {filteredReviews.length > 0 ? (
            <RevCard reviews={filteredReviews} />
          ) : (
            <div className="text-center py-5 bg-white rounded-4 border shadow-sm">
              <p className="text-muted mb-0 fs-5">
                {isEnglish
                  ? "No reviews found matching your search."
                  : "Nessuna recensione trovata per i criteri specificati."}
              </p>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm mt-3 rounded-pill px-3"
                onClick={() => setSearchQuery("")}
              >
                {isEnglish ? "Reset search" : "Azzera ricerca"}
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Reviews;
